"use client"

import { reactiveObserver, useSelector } from "@legendapp/state/react"
import { syncStore$ } from "@/stores/sync.v2.store"
import { Avatar, Box, Indicator } from "@mantine/core"
import { getVerifiedGravatarURL } from "@/lib/avatar/gravatar"
import { getInitialsAvatarStyle } from "@/lib/avatar/initialsAvatarStyle"
import { useEffect, useMemo, useState } from "react"
import type { TLoginUserData } from "@/schemas/user.schema"

const BASE_AVATAR_SIZE = 28

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean).slice(0, 2)
  if (parts.length === 0) return "?"
  return parts.map((part) => part[0]?.toUpperCase() ?? "").join("")
}

export const UserProfileImageWithUserStatus = reactiveObserver(function UserProfileImageWithUserStatus({
  classNames,
  indicatorOffset,
  avatarSize,
  userData,
}: {
  indicatorOffset?: number
  avatarSize?: number
  classNames?: {
    indicator?: string
    avatar?: string
  }
  userData?: TLoginUserData
}) {
  const syncIsCheckedIn = useSelector(() => syncStore$.userStatus.checkedIn.get() ?? false)
  const syncFullName = useSelector(() => syncStore$.userStatus.fullName.get() ?? "")
  const syncEmail = useSelector(() => syncStore$.userStatus.email.get() ?? "")
  const resolvedAvatarSize = avatarSize ?? BASE_AVATAR_SIZE
  const [gravatarURL, setGravatarURL] = useState<string | null>(null)
  const [isGravatarVisible, setIsGravatarVisible] = useState(false)
  const hasHydratedUserData = syncFullName.length > 0 || syncEmail.length > 0
  const fullName = hasHydratedUserData ? syncFullName : (userData?.user.fullName ?? "")
  const email = hasHydratedUserData ? syncEmail : (userData?.user.email ?? "")
  const isCheckedIn = hasHydratedUserData ? syncIsCheckedIn : (userData?.user.checkedIn ?? false)

  const avatarStyle = useMemo(() => getInitialsAvatarStyle(fullName || email), [fullName, email])

  useEffect(() => {
    let isCancelled = false
    setGravatarURL(null)
    setIsGravatarVisible(false)

    getVerifiedGravatarURL({ email, size: resolvedAvatarSize })
      .then((verifiedURL) => {
        if (isCancelled || !verifiedURL) return

        setGravatarURL(verifiedURL)
        requestAnimationFrame(() => {
          if (!isCancelled) setIsGravatarVisible(true)
        })
      })
      .catch(() => {
        if (!isCancelled) setGravatarURL(null)
      })

    return () => {
      isCancelled = true
    }
  }, [email, resolvedAvatarSize])

  return (
    <Indicator
      color={isCheckedIn ? "green.5" : "yellow.5"}
      position="bottom-end"
      className={classNames?.indicator}
      styles={{
        root: { zIndex: 198 },
      }}
      offset={indicatorOffset || 2}
    >
      <Box
        style={{
          position: "relative",
          marginLeft: "0.25rem",
          width: `${resolvedAvatarSize}px`,
          height: `${resolvedAvatarSize}px`,
        }}
      >
        <Avatar
          size={resolvedAvatarSize}
          radius="sm"
          alt={fullName}
          className={classNames?.avatar}
          style={{
            backgroundColor: avatarStyle.backgroundColor,
            border: `1px solid ${avatarStyle.borderColor}`,
          }}
          styles={{
            placeholder: {
              color: avatarStyle.textColor,
              fontWeight: 700,
            },
          }}
        >
          {getInitials(fullName || email)}
        </Avatar>

        {gravatarURL ? (
          <Box
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              width: `${resolvedAvatarSize}px`,
              height: `${resolvedAvatarSize}px`,
              borderRadius: "var(--mantine-radius-sm)",
              backgroundImage: `url("${gravatarURL}")`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              opacity: isGravatarVisible ? 1 : 0,
              filter: isGravatarVisible ? "blur(0px)" : "blur(6px)",
              transform: isGravatarVisible ? "scale(1)" : "scale(1.03)",
              transition: "opacity 220ms ease, filter 260ms ease, transform 260ms ease",
            }}
          />
        ) : null}
      </Box>
    </Indicator>
  )
})
